/**
 * Modal prompting a system administrator (Editor role) for a justification before switching merchant context.
 */

import React, { useState } from "react";
import { DefaultModalLayout } from "../../../components/modal/layouts/default-modal-layout";
import { BUTTON_VARIANTS } from "../../../components/inputs/button-variants";
import { FormField } from "../../../components/form-field/form-field";
import { Input } from "../../../components/inputs/input/input";
import ListLayout from "../../../components/layouts/list-layout/list-layout";
import FontBody2 from "../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR } from "../../../components/fonts/font-props";

export const EDITOR_LOGIN_REASON_MAX_LENGTH = 250;

interface IEditorLoginReasonModal {
    onCloseClick: () => void;
    onLoginAsEditor: (reason: string) => void;
}

const EditorLoginReasonModal: React.FC<IEditorLoginReasonModal> = ({ onCloseClick, onLoginAsEditor }) => {
    const [reason, setReason] = useState<string>("");

    const reasonOverMaxLength = reason.length > EDITOR_LOGIN_REASON_MAX_LENGTH;

    const handleReasonChange = (value: string | number) => {
        setReason(typeof value === "string" ? value : String(value));
    };

    const handleLoginClick = () => {
        if (reasonOverMaxLength) {
            return;
        }
        onLoginAsEditor(reason);
    };

    const buttons = [
        {
            text: 'Cancel',
            variant: BUTTON_VARIANTS.Tertiary,
            onClick: onCloseClick
        },
        {
            text: 'Login as Editor',
            variant: BUTTON_VARIANTS.Primary,
            disabled: reasonOverMaxLength,
            onClick: handleLoginClick
        }
    ];

    return (
        <DefaultModalLayout
            isCloseDisabled={false}
            onCloseClicked={onCloseClick}
            buttons={buttons}
        >
            <div className="editor-login-reason-modal">
                <ListLayout>
                    <FormField
                        label="Reason"
                        instructions={`${reason.length} / ${EDITOR_LOGIN_REASON_MAX_LENGTH} characters`}
                    >
                        <Input
                            type="text"
                            value={reason}
                            placeholder="Enter reason for logging in as editor"
                            onChange={handleReasonChange}
                            onSubmit={handleLoginClick}
                            autoFocus
                        />
                        {reasonOverMaxLength && (
                            <FontBody2 color={FONT_COLOR.SEMANTIC_DANGER}>
                                The maximum length is {EDITOR_LOGIN_REASON_MAX_LENGTH} characters. Shorten your reason
                                to continue.
                            </FontBody2>
                        )}
                    </FormField>
                </ListLayout>
            </div>
        </DefaultModalLayout>
    );
};

export default EditorLoginReasonModal;
